import { useCallback, useState } from 'react'
import { serialFromPayload } from './serialFromPayload'
import { useScanner } from './useScanner'

/**
 * "Scan or type a serial" as one control: a big Scan button that opens the
 * camera in place, and a field for typing. A good scan fills the field and
 * hands it straight to `onSerial` — the person holding the part should not have
 * to press a second button with their other hand.
 */
export function ScanField({
  value,
  onChange,
  onSerial,
  label = 'Scan or type the serial',
  busy = false,
}: {
  value: string
  onChange: (v: string) => void
  onSerial: (serial: string) => void
  label?: string
  busy?: boolean
}): JSX.Element {
  const [problem, setProblem] = useState<string | null>(null)
  const decoded = useCallback(
    (raw: string) => {
      const serial = serialFromPayload(raw)
      if (!serial) {
        setProblem('That is not a Kronos label.')
        return
      }
      setProblem(null)
      onChange(serial)
      onSerial(serial)
    },
    [onChange, onSerial],
  )
  const scanner = useScanner(decoded)
  const looking = scanner.running || scanner.starting

  return (
    <div className="stack" style={{ gap: 10 }}>
      {/* One video element for the hook to attach to, shown only while looking:
          swapping elements mid-start would leave the camera on the old one. */}
      <div className={`scanner scanner--inline${looking ? '' : ' scanner--hidden'}`}>
        <video ref={scanner.videoRef} playsInline muted />
        {scanner.running ? <div className="reticle" /> : null}
        <p className="scan-hint">{scanner.running ? 'Hold the label 10–15 cm away' : 'Starting the camera…'}</p>
      </div>
      <button className={looking ? undefined : 'primary big-action'} disabled={busy || !scanner.supported} onClick={looking ? scanner.stop : scanner.start}>
        {looking ? 'Stop camera' : 'Scan the label'}
      </button>
      <label className="field">
        <span className="field-label">{label}</span>
        <span className="row" style={{ gap: 8 }}>
          <input
            className="serial scan-input"
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            placeholder="K7F38"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={7}
          />
          <button disabled={busy || value.trim().length < 5} onClick={() => onSerial(value.trim())}>
            Use
          </button>
        </span>
      </label>
      {problem || scanner.error ? <p className="critical" style={{ margin: 0 }}>{problem ?? scanner.error}</p> : null}
    </div>
  )
}
